export const CELL_PADDING = 'px-4 py-3';
// Cells inherit the table's regular text colour (bgBase-800); muted lines and links deviate on purpose.
export const CELL_TEXT_MUTED = 'text-sm text-bgBase-700';
export const CELL_LINK = 'text-primary-500 hover:text-primary-600 hover:underline';
export const ROW_ACTION_BUTTON =
	'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-600 [&>svg]:h-4 [&>svg]:w-4';
export const HEADER_CELL = 'px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-bgBase-800';

export const SELECTION_COLUMN_WIDTH = 48;

const CELL_HORIZONTAL_PADDING = 32;
// Until the user drags it, the first column grows with its content up to this width (px).
const FIRST_COLUMN_DEFAULT_MAX_WIDTH = 320;

export const ALIGN_CLASS = {
	left: 'text-left',
	center: 'text-center',
	right: 'text-right',
};

export const JUSTIFY_CLASS = {
	left: '',
	center: 'justify-center',
	right: 'justify-end',
};

// Casts a shadow onto the content scrolling beneath the frozen actions column.
const STICKY_RIGHT_SHADOW =
	'before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-3 before:bg-[linear-gradient(to_right,transparent,rgba(15,23,42,0.09))]';

/**
 * Sticky cells sit above the scrolled content, so they need an opaque
 * background of their own — inheriting the row's translucent hover or selected
 * tint would let the scrolled-under content show through.
 *
 * @param {Object} state
 * @param {boolean} [state.isSelected]
 * @param {boolean} [state.isHighlighted]
 * @param {boolean} [state.isExpanded]
 * @returns {string} Tailwind classes for the sticky cell background.
 */
export function stickyCellBackground({ isSelected = false, isHighlighted = false, isExpanded = false } = {}) {
	if (isHighlighted) return 'bg-primary-100';
	// Tailwind scans for literal class names, so these cannot be built from shared constants.
	if (isSelected) return 'bg-[#f6f4ff]';
	if (isExpanded) return 'bg-[#faf9ff]';
	return 'bg-white group-hover/row:bg-[#fbfaff]';
}

/**
 * Freezes the first column: it carries the row's identity, which has to stay
 * readable while the rest scrolls away.
 *
 * @param {Array<Object>} columns
 * @param {boolean} firstColumnSticky
 * @returns {Array<Object>} The same columns with the first one's `sticky` resolved.
 */
export function resolveSticky(columns, firstColumnSticky) {
	if (!firstColumnSticky || columns.length === 0 || columns[0].sticky) return columns;
	return [{ ...columns[0], sticky: 'left' }, ...columns.slice(1)];
}

/**
 * Sizes the first column: the user-dragged width, else the column's own numeric width, else its
 * content capped at a default. The cell content gets a box of `contentWidth`/`contentMaxWidth`
 * because an auto-layout table ignores a cell's width once its content is wider.
 *
 * @param {Array<Object>} columns
 * @param {number|null} width - User-dragged outer width in px.
 * @returns {Array<Object>}
 */
export function applyFirstColumnWidth(columns, width) {
	if (columns.length === 0) return columns;
	const [first, ...rest] = columns;
	const fixedWidth = width ?? (typeof first.width === 'number' ? first.width : null);
	if (fixedWidth) {
		const sized = {
			...first,
			grow: false,
			width: fixedWidth,
			minWidth: fixedWidth,
			stickyWidth: fixedWidth,
			contentWidth: fixedWidth - CELL_HORIZONTAL_PADDING,
		};
		return [sized, ...rest];
	}
	if (first.grow) return columns;
	return [{ ...first, contentMaxWidth: FIRST_COLUMN_DEFAULT_MAX_WIDTH - CELL_HORIZONTAL_PADDING }, ...rest];
}

/**
 * An auto-layout table hands spare width only to columns without a declared width. So once any
 * column grows, every other column is pinned: to its declared width, else shrunk to its content.
 *
 * @param {Array<Object>} columns
 * @returns {Array<Object>}
 */
export function applyGrow(columns) {
	if (!columns.some((column) => column.grow)) return columns;
	return columns.map((column) => (column.grow ? column : { ...column, width: column.width ?? column.minWidth ?? 1 }));
}

const numericWidth = (column) => {
	const width = column.minWidth ?? column.width;
	return typeof width === 'number' ? width : 0;
};

/**
 * Width below which the table scrolls horizontally instead of squeezing: the sum of the visible
 * columns' declared widths. Columns without a numeric width count as 0, so a table that declares
 * none never scrolls.
 *
 * @param {Array<Object>} columns - Visible columns.
 * @param {boolean} hasSelection - Whether the leading checkbox column is rendered.
 * @returns {number} 0 when no column declares a width.
 */
export function tableMinWidth(columns, hasSelection) {
	const total = columns.reduce((sum, column) => sum + numericWidth(column), 0);
	return total > 0 && hasSelection ? total + SELECTION_COLUMN_WIDTH : total;
}

/**
 * Width, and the offset that pins a frozen column: a sticky-left column starts
 * where the previous one ends, so every preceding sticky column must declare
 * `stickyWidth`.
 *
 * @param {Array<Object>} columns - All columns, needed to sum preceding sticky widths.
 * @param {number} index - Index of the column being positioned.
 * @param {boolean} hasSelection - Whether the leading checkbox column is rendered.
 * @returns {Object} Inline style for the cell.
 */
export function columnStyle(columns, index, hasSelection) {
	const column = columns[index];

	const left =
		column.sticky === 'left'
			? columns
					.slice(0, index)
					.filter((col) => col.sticky === 'left')
					.reduce((offset, col) => offset + (col.stickyWidth ?? 0), hasSelection ? SELECTION_COLUMN_WIDTH : 0)
			: undefined;

	return {
		width: column.width,
		minWidth: column.minWidth,
		left,
		right: column.sticky === 'right' ? 0 : undefined,
	};
}

/**
 * Border and shadow that separate a frozen column from the scrolling area.
 * A table that never scrolls gets neither: the edge would read as an arbitrary
 * rule between two columns.
 *
 * @param {'left'|'right'} [sticky]
 * @param {boolean} [scrolls] - Whether the table can scroll horizontally.
 * @returns {string} Tailwind classes.
 */
export function stickyEdgeClass(sticky, scrolls = false) {
	if (!scrolls) return '';
	if (sticky === 'left') return 'border-r border-bgBase-500';
	if (sticky === 'right') return `border-l border-bgBase-500 ${STICKY_RIGHT_SHADOW}`;
	return '';
}
